"use client";

import { ReactNode } from "react";
import {
  currencify,
  formatDate,
  formatNumValue,
} from "@repo/shared-utils/num-utils";
import { LABEL_WITH_Rs_SYMBOLS } from "./StockTechnicalSummaryConstant";

export interface IndicatorEntry {
  label?: string;
  value?: string | number;
  color?: string;
  icon?: ReactNode;
}

/**
 * Formats date value for display, handling empty/invalid dates
 * @param value - The date value to format
 * @returns Formatted date string or "-" for empty/invalid dates
 */
const formatDateValue = (value: string | number | undefined): string => {
  const dateValue = value ?? "";
  if (dateValue === "" || dateValue === "-") {
    return "-";
  }
  return formatDate(dateValue, { format: "dmy" });
};

interface IndicatorTableProps {
  entries: IndicatorEntry[];
  label?: string;
  priceValue?: number;
}

const StockTable = ({
  entries,
  label = "",
  priceValue = 0,
}: IndicatorTableProps) => {
  const filteredEntries = (entries || []).filter(
    (item) => item?.label && item?.value !== undefined && item?.value !== null,
  );

  if (filteredEntries.length === 0) return null;

  const isEMA = label.toLowerCase() === "ema";

  if (isEMA) {
    const half = Math.ceil(filteredEntries.length / 2);
    const leftColumn = filteredEntries.slice(0, half);
    const rightColumn = filteredEntries.slice(half);

    return (
      <div
        className={`grid grid-cols-2 overflow-hidden rounded-md border border-gray-300 ${
          label === "ema" ? "md:max-w-[682px]" : ""
        }`}
      >
        {[leftColumn, rightColumn].map((column, colIndex) => (
          <div
            key={`ema-col-${colIndex}`}
            className={`flex flex-col divide-y divide-gray-300 ${
              colIndex === 0 ? "border-r border-gray-300" : ""
            }`}
          >
            {column.map((item, index) => (
              <div
                key={`ema-row-${colIndex}-${index}`}
                className={`flex justify-between px-4 py-2 text-sm ${
                  index % 2 === 0 ? "bg-gray-50" : "bg-white"
                }`}
              >
                <span className="text-sm text-gray-900 md:text-base">
                  {item.label}
                </span>
                <span
                  className={`text-sm md:text-base ${
                    Number(priceValue ?? 0) > Number(item?.value ?? 0)
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatNumValue(Number(item?.value))}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  const isDesktopOnlyLayout = ["beta", "dnVol"].includes(label);

  return (
    <div
      className={`mb-3 w-full flex-wrap rounded-[4px] border-l border-t border-blue-700 ${
        isDesktopOnlyLayout
          ? "md:flex"
          : label !== "rSupport" && label !== "pChgAnalysis"
            ? "flex"
            : "md:flex"
      } ${["beta", "pChgAnalysis"].includes(label) ? "md:max-w-[682px]" : ""}`}
    >
      {filteredEntries.map((item, index) => {
        let displayValue: string = "";

        if (typeof item?.value === "number") {
          const symbol = LABEL_WITH_Rs_SYMBOLS.includes(item?.label ?? "");
          const value = formatNumValue(Number(item?.value ?? 0));
          const suffix = item?.icon ? "%" : "";
          displayValue = `${
            symbol
              ? currencify(Number(value), {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : value
          }${suffix}`;
        } else if (item?.label === "Date") {
          displayValue = formatDateValue(item?.value);
        } else {
          displayValue = item?.value ?? "";
        }

        return (
          <div
            key={"stockTableEntry-" + index}
            className={`box-border flex border-b border-r ${
              isDesktopOnlyLayout
                ? "md:w-1/2"
                : label !== "rSupport" && label !== "pChgAnalysis"
                  ? "w-1/2"
                  : "md:w-1/2"
            }`}
          >
            <div className="bg-lgray-100 w-[150px] border-r p-[8px] text-sm text-gray-700">
              {item?.label}
            </div>
            <div
              className={`flex w-1/2 gap-1 p-[8px] text-sm font-medium ${
                ["ema", "pChgAnalysis"].includes(label)
                  ? typeof item?.value === "number" && item.value > 0
                    ? "text-green-500"
                    : "text-red-600"
                  : "text-gray-900"
              }`}
            >
              {displayValue}
              {item?.icon && <span className={item?.color}>{item?.icon}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StockTable;
